import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { GasketWorkflowStatus, MapareRow } from '@lyra/shared';
import { Button } from '@/components/ui/button';
import { StatusLabel, type StatusTone } from '@/components/ui/StatusLabel';
import { LiveRegion } from '@/components/a11y/LiveRegion';
import { formatNumberRo } from '@/lib/format';
import { useSessionDrawerPhase } from '@/components/shell/sessionDrawerPresence';
import { buildRenderItems, groupResults, type GroupedResults, type ResultsRenderItem } from './lib/rows';
import { TABLE_BUCKET_LABELS } from './lib/labels';
import { rowsNeedingRetry } from './lib/classify';
import { CorrectionPanel, useDisclosureFocusTrap } from './CorrectionPanel';
import { useBulkResolve, useRetryRows } from './api/reviewHooks';
import { RetryBar, RetryRowButton, useRetryOrchestrator } from './RetryBar';

/** The review buckets whose rows carry a bulk-selection checkbox + „Selectează
 *  tot". Both „De verificat" (candidates/uncertain code) and „Fără cod final"
 *  (no_match) are bulk-actionable, so selection spans both (#2). */
const SELECTABLE_GROUPS: (keyof GroupedResults)[] = ['deVerificat', 'faraCorespondent'];

/**
 * Direction-B grid template shared by the static header row and every `row`
 * render-item, so columns line up: a leading selection-checkbox slot (Task
 * 5 — only populated for „De verificat" rows, empty elsewhere so columns
 * still align across groups), denumire parteneră, cod sursă, cod MEDOC,
 * denumire MEDOC, cod BD, încredere, status, then a fixed-width trailing
 * slot holding the expand toggle and — for rows `rowsNeedingRetry` selects
 * (Task 6) — a „Reîncearcă" icon button next to it. Widened from 32px to
 * 64px vs. 3c-1/Task 5 to fit both controls without crowding.
 */
// Tracks: [select] name code VALUE codMedoc denumire confidence status [actions]
// (Cod BD dropped from the always-visible grid — it survives in the correction
//  panel; dropping it widens the remaining columns so headers never truncate.)
// Column set: [select] name, cod sursă, valoare, Cod MEDOC, Cod BD MEDOC,
// Denumire MEDOC, Încredere (round gauge), status, [actions]. Both Mapare view
// routes (/mapari/:id/tabel — this grid — and /mapari/:id/validare*) render
// inside PageCanvas width='full' (max-w-none), so the grid spreads across the
// full viewport and the two flex columns absorb the slack — the old 1024px-canvas
// premise no longer applies. Intrinsic minimum ≈ 1084px (692 fixed + 320 flex-min
// + 72 gaps); min-w-[1080px] matches it so the overflow-x-auto scroll kicks in at
// the real floor on a narrow window rather than a stale ~1000px.
const GRID_TEMPLATE = '36px minmax(160px,1.4fr) 100px 84px 112px 108px minmax(160px,1.4fr) 76px 124px 72px';

/**
 * Accessibility remediation (2026-08-19 Slice B, Task 2). Shared id
 * generator so `RowExpandToggle`'s `aria-controls` and the actual detail
 * wrapper's `id` (whichever of `DetailItem`'s two branches renders — the
 * `CorrectionPanel` or the plain „Rezolvate" reasoning view) always agree,
 * without either side re-deriving the string independently. Set
 * unconditionally on the toggle regardless of expanded state — the target
 * only exists in the DOM once the row is expanded (this is a virtualized,
 * lazily-mounted disclosure, not a CSS-hidden one), which is the same
 * accepted trade-off most lazy-mounted accordion/disclosure widgets make.
 */
function panelId(rowId: number): string {
  return `mapari-row-panel-${rowId}`;
}

/**
 * Row-level status label + dot color. Ported (Rule Zero, best-effort) from
 * v1 `ResultsTable.tsx`'s `getWorkflowBadge`, using Direction B's semantic
 * dot/text idiom (same tokens as `MapariListPage`'s `STATUS_META`) instead
 * of v1's literal-color pill classes.
 *
 * Fix 4 (reviewed rows keep pre-review status label): now that 3c-2/3c-3
 * wire up real review actions (`CorrectionPanel`, bulk-resolve), a row
 * routinely carries a `reviewResolution` stamped by an operator decision
 * AFTER its original `workflowStatus` was set — e.g. a confirmed `ambiguous`
 * row keeps `workflowStatus:'ambiguous'` forever, only `reviewResolution`
 * changes. The old code only special-cased `reviewResolution === 'unresolved'`
 * and otherwise fell straight into the `byWorkflow` lookup, so a confirmed
 * ambiguous row still showed „Ambiguu"/warn-dot under the „Rezolvate"
 * section — the label contradicted the section it was grouped into. Per
 * `ReviewResolution`'s semantics (`confirmed_primary`/`selected_alternate`/
 * `unresolved`), ALL THREE now relabel up front, before the
 * workflow-specific lookup: `confirmed_primary` → „Confirmat" (ok, matches
 * v1's non-partner-mapping `resolved` label for a confirmed row);
 * `selected_alternate` → „Rezolvat manual" (ok, matches v1's `ambiguous`+
 * resolved label, generalized — the row now carries a final code regardless
 * of which workflow status it started from); `unresolved` → „Nerezolvat"
 * (mute, unchanged from before — `groupResults` buckets these into „Fără
 * corespondent", so the label must say so too). A resolved-status row with
 * NO `reviewResolution` set still falls through to the unchanged
 * `byWorkflow`/status fallback below, exactly as today.
 */
function rowStatusMeta(row: MapareRow): { label: string; dotClass: string } {
  if (row.status === 'error') return { label: 'Eroare', dotClass: 'bg-bad' };
  if (row.status === 'pending') return { label: 'În așteptare', dotClass: 'bg-mute' };
  if (row.reviewResolution === 'confirmed_primary') return { label: 'Confirmat', dotClass: 'bg-ok' };
  if (row.reviewResolution === 'selected_alternate') return { label: 'Rezolvat manual', dotClass: 'bg-ok' };
  if (row.reviewResolution === 'unresolved') return { label: 'Nerezolvat', dotClass: 'bg-mute' };

  const byWorkflow: Partial<Record<GasketWorkflowStatus, { label: string; dotClass: string }>> = {
    resolved: { label: 'Rezolvat', dotClass: 'bg-ok' },
    ambiguous: { label: 'Ambiguu', dotClass: 'bg-warn' },
    needs_review: { label: 'Revizuire', dotClass: 'bg-warn' },
    no_match: { label: TABLE_BUCKET_LABELS.faraCorespondent, dotClass: 'bg-mute' },
    low_confidence: { label: 'Încredere scăzută', dotClass: 'bg-warn' },
  };
  if (row.workflowStatus && byWorkflow[row.workflowStatus]) return byWorkflow[row.workflowStatus]!;

  return { label: row.status === 'edited' ? 'Modificat' : 'Clasificat', dotClass: 'bg-ok' };
}

/** `rowStatusMeta`'s dot token → shared `StatusLabel` tone (2026-08-19
 *  remediation Slice D, Task 3: DESIGN.md's dot+label idiom, not the
 *  wash-filled `Pill` chip this used to re-wrap it into). */
const STATUS_TONE: Record<string, StatusTone> = {
  'bg-ok': 'ok',
  'bg-warn': 'warn',
  'bg-bad': 'bad',
  'bg-mute': 'mute',
};

function StatusCell({ row }: { row: MapareRow }) {
  const meta = rowStatusMeta(row);
  return <StatusLabel tone={STATUS_TONE[meta.dotClass] ?? 'mute'} label={meta.label} />;
}

/**
 * Încredere: `Math.round(confidence * 100)%` + a thin inline meter (same
 * track/fill idiom as `LiveProgress.tsx`'s progress bar — `bg-rule-soft`
 * track, `bg-ok`/`bg-warn` fill), warn-colored below 0.7 (Component
 * Requirements). Not v1's `getConfidenceLevel5` pill: that helper's classes
 * are literal, dark-mode-unaware Tailwind colors (`bg-green-500/15
 * text-green-700`, no `dark:` variant), inconsistent with every other
 * Direction-B token (`ok`/`warn`/`bad`/`mute`) used throughout Tasks 7-9 —
 * a deliberate deviation, not an oversight. A 0 score (or pending/error rows)
 * means "no signal" (mirrors v1's same `hasScore` guard), shown as a dash
 * rather than an alarming 0%.
 */
function ConfidenceCell({ row }: { row: MapareRow }) {
  const displayConfidence = row.resolutionStrategy === 'confirmed_mapping' ? 1 : row.confidence;
  const hasScore = row.status !== 'error' && row.status !== 'pending' && displayConfidence > 0;
  if (!hasScore) {
    return <span className="text-[13.5px] text-ink-3">—</span>;
  }
  const pct = Math.round(displayConfidence * 100);
  // Compact round gauge, color-coded (owner: "make it round"). A small donut —
  // track + a colored arc for the level — so the column stays tight; the exact
  // percentage lives in the accessible name + hover title. Tones: green / amber / red.
  const tone = displayConfidence >= 0.85 ? 'stroke-ok' : displayConfidence >= 0.6 ? 'stroke-warn' : 'stroke-bad';
  const CIRC = 2 * Math.PI * 7; // r = 7
  return (
    <div
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Încredere ${pct}%`}
      title={`${pct}%`}
      className="inline-flex"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" className="-rotate-90" aria-hidden="true">
        <circle cx="9" cy="9" r="7" fill="none" strokeWidth="2.5" className="stroke-rule-soft" />
        <circle
          cx="9"
          cy="9"
          r="7"
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={CIRC * (1 - displayConfidence)}
          className={tone}
        />
      </svg>
    </div>
  );
}

const columnHelper = createColumnHelper<MapareRow>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Denumire parteneră',
    cell: (info) => (
      <span className="block truncate text-[13.5px] text-ink" title={info.getValue()}>
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('code', {
    header: 'Cod sursă',
    cell: (info) => <span className="font-mono text-[12.5px] text-ink-2">{info.getValue()}</span>,
  }),
  // B5 (parity — v1 showed the source „Valoare"/price column so an operator can
  // cross-check prices while classifying). Kept next to the other source columns.
  columnHelper.accessor((row) => row.value || '', {
    id: 'value',
    header: 'Valoare',
    cell: (info) => (
      <span className="block truncate font-mono text-[12.5px] tabular-nums text-ink-2" title={info.getValue()}>
        {info.getValue() || '—'}
      </span>
    ),
  }),
  columnHelper.accessor((row) => row.codMedoc || row.provisionalCodMedoc || '—', {
    id: 'codMedoc',
    header: 'Cod MEDOC',
    cell: (info) => {
      const row = info.row.original;
      const isProvisional = !row.codMedoc && Boolean(row.provisionalCodMedoc);
      return (
        <span className={`font-mono text-[12.5px] ${isProvisional ? 'italic text-ink-3' : 'text-ink-2'}`}>
          {info.getValue()}
        </span>
      );
    },
  }),
  columnHelper.accessor((row) => row.codBdMedoc || row.provisionalCodBdMedoc || '—', {
    id: 'codBdMedoc',
    header: 'Cod BD MEDOC',
    cell: (info) => {
      const row = info.row.original;
      const isProvisional = !row.codBdMedoc && Boolean(row.provisionalCodBdMedoc);
      return (
        <span className={`font-mono text-[12.5px] ${isProvisional ? 'italic text-ink-3' : 'text-ink-2'}`}>
          {info.getValue()}
        </span>
      );
    },
  }),
  columnHelper.accessor((row) => row.denumireMedoc || row.provisionalDenumireMedoc || '—', {
    id: 'denumireMedoc',
    header: 'Denumire MEDOC',
    cell: (info) => {
      const row = info.row.original;
      const isProvisional = !row.denumireMedoc && Boolean(row.provisionalDenumireMedoc);
      const value = info.getValue();
      return (
        <span className={`block truncate text-[13.5px] ${isProvisional ? 'italic text-ink-3' : 'text-ink'}`} title={value}>
          {value}
        </span>
      );
    },
  }),
  columnHelper.display({
    id: 'confidence',
    header: 'Încredere',
    cell: (info) => <ConfidenceCell row={info.row.original} />,
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) => <StatusCell row={info.row.original} />,
  }),
];

/**
 * `'detail'` estimates branch on `group` (Task 5): the „Rezolvate" reasoning-
 * only detail is short, but the correction panel (De verificat / Fără
 * corespondent / Erori, and a re-opened „Rezolvate" row) is much taller —
 * reasoning + primary suggestion + alternates + a nomenclator combobox.
 * Real height always comes from `measureElement`; this only picks a starting
 * estimate close enough that the initial (pre-measure) layout isn't janky.
 */
function estimateItemSize(item: ResultsRenderItem): number {
  switch (item.type) {
    case 'header':
      return 40;
    case 'collapsed-toggle':
      return 40;
    case 'row':
      return 46;
    case 'detail':
      return item.group === 'rezolvate' ? 100 : 340;
  }
}

function HeaderItem({
  item,
  onCollapseRezolvate,
  allSelected,
  onToggleSelectAll,
  retrySlot,
  readOnly,
}: {
  item: Extract<ResultsRenderItem, { type: 'header' }>;
  onCollapseRezolvate: () => void;
  allSelected: boolean;
  onToggleSelectAll: () => void;
  /** Task 6: the „Erori" header's bulk „Reîncearcă toate" control (`RetryBar`).
   *  Renders `ml-auto` — safe alongside `collapsible`/`selectable`, since
   *  neither flag is ever set on the „Erori" header. */
  retrySlot?: ReactNode;
  /** Plan 3c-3 Task 6: true on a finalized run — hides the „Selectează tot"
   *  affordance (bulk-resolve would 409 server-side). */
  readOnly?: boolean;
}) {
  const accent =
    item.key === 'header-deVerificat'
      ? 'bg-warn'
      : item.key === 'header-faraCorespondent'
        ? 'bg-mute'
        : item.key === 'header-rezolvate'
          ? 'bg-ok'
          : item.key === 'header-erori'
            ? 'bg-bad'
            : 'bg-ink-3';
  return (
    // Fix round item 3: this banner is the sole content of its parent
    // `role="row"` wrapper (the virtualized item wrapper in `ResultsTable`)
    // — `role="gridcell"` here satisfies row's required-owned-elements rule
    // without adding any element (this div already existed).
    <div
      role="gridcell"
      className="flex h-full items-center gap-2.5 border-y border-rule bg-wash px-4 text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-2"
    >
      <span className={`h-3.5 w-[3px] shrink-0 rounded-full ${accent}`} aria-hidden />
      <span>{item.label}</span>
      <span className="rounded-full border border-rule bg-bg px-2 py-0.5 text-[11.5px] font-semibold normal-case tabular-nums text-ink-3">{formatNumberRo(item.count)}</span>
      {item.hint && <span className="font-normal normal-case text-[11.5px] text-ink-3">· {item.hint}</span>}
      {item.selectable && !readOnly && (
        <button
          type="button"
          onClick={onToggleSelectAll}
          className="text-[12.5px] font-medium normal-case text-teal hover:underline"
        >
          {allSelected ? 'Deselectează tot' : 'Selectează tot'}
        </button>
      )}
      {item.collapsible && (
        <button
          type="button"
          onClick={onCollapseRezolvate}
          className="ml-auto text-[12.5px] font-medium normal-case text-teal hover:underline"
        >
          Ascunde ↑
        </button>
      )}
      {retrySlot}
    </div>
  );
}

/**
 * Expand/collapse control for a row's detail item. Reviewable rows (De
 * verificat / Fără corespondent / Erori) always expand into the correction
 * panel — „Revizuiește" — regardless of whether `reasoning` is present (the
 * panel is useful even with no model reasoning: manual nomenclator search,
 * mark-unresolved, etc. are always available). „Rezolvate" rows keep the
 * reasoning-toggle copy/semantics from 3c-1, and are still always openable
 * (spec §5: resolved rows re-openable via the panel's „Corectează" control).
 */
const RowExpandToggle = forwardRef<
  HTMLButtonElement,
  {
    group: keyof GroupedResults;
    expanded: boolean;
    onToggle: () => void;
    /** The id of the detail wrapper this toggle discloses (`panelId(row.id)`) —
     *  wired to `aria-controls` so a screen reader can jump straight there. */
    panelId: string;
  }
>(function RowExpandToggle({ group, expanded, onToggle, panelId }, ref) {
  const label =
    group === 'rezolvate'
      ? expanded
        ? 'Ascunde motivația'
        : 'Arată motivația'
      : expanded
        ? 'Ascunde revizuirea'
        : 'Revizuiește';
  return (
    <button
      ref={ref}
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={panelId}
      aria-label={label}
      title={label}
      className="flex size-6 items-center justify-center rounded text-ink-3 hover:text-ink-2"
    >
      {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
    </button>
  );
});

/**
 * Detail render-item (Task 5). Branches on the row's group:
 *   - „Rezolvate": the 3c-1 plain reasoning view, plus a quiet „Corectează"
 *     control that swaps this same detail slot into the `CorrectionPanel`
 *     (`correcting`, lifted to `ResultsTable` state so re-opening a
 *     different row resets it) — spec §5's "resolved rows re-openable".
 *   - Everything else (De verificat / Fără corespondent / Erori): always the
 *     `CorrectionPanel` — these rows need an operator decision.
 * `onDone` (fired on a successful mutation) collapses the row: the panel's
 * own action already invalidates `queryKeys.run(runId)`, so the row will
 * re-bucket on the next settled fetch — no reason to keep an open detail
 * pointed at a row that's about to move to a different group.
 */
function DetailItem({
  item,
  runId,
  correcting,
  onStartCorrecting,
  onDone,
  onRequestClose,
  readOnly,
}: {
  item: Extract<ResultsRenderItem, { type: 'detail' }>;
  runId: number;
  correcting: boolean;
  onStartCorrecting: () => void;
  onDone: () => void;
  /** Escape inside the `CorrectionPanel` — closes the row AND returns focus
   *  to its toggle (`ResultsTable`'s `handleRequestClose`), distinct from
   *  `onDone` (a successful mutation, which collapses the row too but has
   *  nowhere sensible to return focus to — the row is about to re-bucket). */
  onRequestClose: () => void;
  /** Plan 3c-3 Task 6: true on a finalized run — the panel still opens (view
   *  reasoning + candidates) but every mutating control inside it is disabled
   *  (the server 409s entry edits on a finalized run, T2), and the „Rezolvate"
   *  plain-reasoning view drops its „Corectează" toggle (nothing to correct
   *  into). */
  readOnly?: boolean;
}) {
  // Fix round item 2 (2026-08-19): called unconditionally, before either
  // branch below, so this component's hook-call order never depends on
  // which branch a given render takes (rules-of-hooks) — the same „Rezolvate"
  // row swaps between the two branches in place (`onStartCorrecting` flips
  // `correcting` without unmounting `DetailItem`), so the hook must be
  // called every render regardless. When the `CorrectionPanel` branch
  // renders, `panelRef`/`headingRef` are simply never attached to a DOM
  // node here — `CorrectionPanel` owns its own separate instance of this
  // same hook internally, so there's no double focus-stealing.
  const { panelRef, headingRef, handleKeyDown } = useDisclosureFocusTrap(onRequestClose);

  if (item.group !== 'rezolvate' || correcting) {
    return (
      // Fix round item 3: sole content of the parent `role="row"` wrapper —
      // `role="gridcell"` added to this pre-existing div, no new element.
      <div id={panelId(item.row.id)} role="gridcell" className="h-full border-b border-rule-soft bg-wash/60 px-3 py-3">
        <CorrectionPanel
          row={item.row}
          runId={runId}
          onDone={onDone}
          onRequestClose={onRequestClose}
          readOnly={readOnly}
        />
      </div>
    );
  }
  return (
    // Fix round item 3: same rationale as the branch above — role added to
    // the existing div, no new element.
    <div
      id={panelId(item.row.id)}
      role="gridcell"
      ref={panelRef}
      onKeyDown={handleKeyDown}
      className="h-full border-b border-rule-soft bg-wash/60 px-3 py-3 text-[13.5px] leading-relaxed text-ink-2"
    >
      {/* Fix round item 2 (2026-08-19 review): this branch shares its
          toggle, `panelId`, and `aria-controls` target with the
          `CorrectionPanel` branch above — it now shares the same focus-in /
          Tab-trap / Escape contract too, via `useDisclosureFocusTrap`
          (see CorrectionPanel.tsx). The heading below is the focus-in
          target, mirroring `CorrectionPanel`'s own sr-only `<h3>`. */}
      <h3 ref={headingRef} tabIndex={-1} className="sr-only">
        Motivația pentru „{item.row.name}"
      </h3>
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-[11.5px] font-semibold uppercase tracking-wide text-ink-3">Motivație</p>
        {!readOnly && (
          <button
            type="button"
            onClick={onStartCorrecting}
            className="text-[12.5px] font-medium text-teal hover:underline"
          >
            Corectează
          </button>
        )}
      </div>
      <p className="whitespace-pre-wrap">{item.row.reasoning}</p>
    </div>
  );
}

export interface ResultsTableProps {
  /** Already-filtered/sorted rows (the page's `visibleRows`). */
  rows: MapareRow[];
  /** The run these rows belong to — threaded into `CorrectionPanel` and `useBulkResolve`. */
  runId: number;
  /**
   * Plan 3c-3 Task 6: true when the run is finalized, matching the server-
   * enforced lock (T2 409s entry edits on a finalized run). While true:
   *   - no row-selection checkboxes and no bulk action bar (bulk-resolve would 409)
   *   - the „Erori" header's bulk retry control and every per-row retry button
   *     are hidden (retry writes would 409)
   *   - a row's detail still opens, but renders `CorrectionPanel` with
   *     `readOnly` (its mutating controls are disabled; reasoning + candidate
   *     display stays visible), and the „Rezolvate" plain-reasoning view drops
   *     its „Corectează" toggle
   */
  readOnly?: boolean;
  /**
   * #3: true when the active status filter IS the „Rezolvate" tab. „Rezolvate"
   * collapses by default in the „Toate" view (it's large and would bury the
   * rows needing attention), but when the operator has filtered *to* it, that
   * collapse is nonsense — they asked for exactly those rows — so it renders
   * fully expanded and without the collapse affordance.
   */
  rezolvateFilterActive?: boolean;
}

/**
 * `/mapari/:id` — virtualized, status-grouped, INTERACTIVE results list
 * (Task 5, upgrading 3c-1's Task 10 view-only table). Ports the VIEW parts
 * of v1 `ResultsTable.tsx` (column set, confidence display, workflow/status
 * label) into Direction B's editorial idiom — hairline `border-rule-soft`
 * row dividers, no boxed cards, no header fill, mono for codes — and adds
 * the 3c-2 correction/bulk-action layer on top: the reasoning-expand row
 * becomes a `CorrectionPanel` for reviewable rows, „De verificat" rows carry
 * a selection checkbox, and a bulk action bar appears once ≥1 is selected.
 *
 * Built on TanStack Table v8 (headless, column defs + cell rendering only —
 * no sorting/pagination row models, the page already filters/sorts) and
 * TanStack Virtual v3, driven by a SINGLE `useVirtualizer` over a flat list
 * of render-items (`buildRenderItems`, `lib/rows.ts`): section headers, row
 * items, and — right after an expanded row — its detail item, all windowed
 * together. This is what keeps ≥1k rows smooth: only the visible range
 * (+ overscan) ever mounts to the DOM, regardless of how many rows a
 * section holds. The correction panel is a much taller, variable-height
 * detail than 3c-1's plain reasoning text — `estimateItemSize` picks a
 * bigger starting guess for it, but `measureElement` (attached to every
 * render-item, unchanged from 3c-1) still owns the real height, so a panel
 * expanded near the bottom of a long list measures and scrolls correctly.
 *
 * Task 6 adds retry-failed-rows: a bulk „Reîncearcă toate (N)" control in
 * the „Erori" header plus a per-row „Reîncearcă" icon (see `RetryBar.tsx`),
 * gated on an active instance session per spec decision D3 — see that
 * file's header comment for the full rationale.
 */
export function ResultsTable({ rows, runId, readOnly = false, rezolvateFilterActive = false }: ResultsTableProps) {
  /**
   * I3 fix (2026-08-20): the gutter must clear the drawer's real footprint,
   * not every corner state it publishes. `SessionDrawer` was rebuilt so its
   * `prompt` phase (proactive „Pornește instanța" offer, no active session)
   * is now a slim `size-12` corner FAB that covers only the actions column —
   * it never reaches STATUS, so reserving 368px for it would cost the grid
   * that width for nothing. Only the `'drawer'` phase (an active session:
   * provisioning/ready/failed) still paints the full ~352px card that
   * overlaps a wide grid. `useSessionDrawerPhase` (published by the drawer
   * itself via `SessionDrawerPresenceProvider`, mounted in `AppShell`) is
   * read directly and compared to `'drawer'` below, rather than using the
   * coarser `useSessionDrawerOccupiesCorner` (true for `'prompt'` too).
   */
  const drawerPhase = useSessionDrawerPhase();
  const [rezolvateExpanded, setRezolvateExpanded] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  /** Only meaningful while `expandedRowId` points at a „Rezolvate" row: that
   *  row's detail defaults to the plain reasoning view, and flips to the
   *  `CorrectionPanel` once its „Corectează" control is clicked. */
  const [correctingRezolvatId, setCorrectingRezolvatId] = useState<number | null>(null);
  /** Selection lives here (not in `lib/rows.ts`) — it's pure interaction
   *  state, orthogonal to how rows are grouped/rendered. Default unselected
   *  (Direction-B, explicit) — no v1 WizardListStep select-all-by-default. */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const grouped = useMemo(() => groupResults(rows), [rows]);
  const allRows = useMemo(
    () => [...grouped.deVerificat, ...grouped.faraCorespondent, ...grouped.rezolvate, ...grouped.erori],
    [grouped]
  );
  /** Row id → 1-based position among `allRows` (Task 2: `aria-rowindex`
   *  arithmetic). Built once from the same fixed section order `allRows`
   *  concatenates in, so it stays correct however the virtualizer windows
   *  the list — a collapsed „Rezolvate" row still has a stable index, it's
   *  just not currently rendered. */
  const rowIndexById = useMemo(() => {
    const map = new Map<number, number>();
    allRows.forEach((row, i) => map.set(row.id, i));
    return map;
  }, [allRows]);
  const renderItems = useMemo(
    () => buildRenderItems(grouped, {
      rezolvateExpanded: rezolvateExpanded || rezolvateFilterActive,
      expandedRowId,
      rezolvateCollapsible: !rezolvateFilterActive,
    }),
    [grouped, rezolvateExpanded, rezolvateFilterActive, expandedRowId]
  );

  /** Task 2: one `RowExpandToggle` DOM node per currently-mounted row,
   *  keyed by row id — lets `handleRequestClose` return focus to the
   *  trigger that opened a panel even though virtualization means the
   *  actual button instance may have unmounted/remounted since. */
  const toggleRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const registerToggleRef = useCallback(
    (rowId: number) => (el: HTMLButtonElement | null) => {
      if (el) toggleRefs.current.set(rowId, el);
      else toggleRefs.current.delete(rowId);
    },
    []
  );

  const toggleExpand = useCallback((id: number) => {
    setExpandedRowId((cur) => (cur === id ? null : id));
    // Switching (or collapsing) which row is expanded always drops any
    // in-progress „Corectează" override — it only ever applies to whichever
    // „Rezolvate" row is currently open.
    setCorrectingRezolvatId(null);
  }, []);

  const collapseRow = useCallback(() => {
    setExpandedRowId(null);
    setCorrectingRezolvatId(null);
  }, []);

  /** Task 2: Escape inside a `CorrectionPanel` — collapse the row (same as
   *  `collapseRow`) AND return focus to the toggle that opened it, so the
   *  operator lands back where they started instead of on `<body>`. Distinct
   *  from `collapseRow`/`onDone` (a successful mutation), which has no
   *  single sensible focus target — the row is about to re-bucket into a
   *  different section on the next settled fetch. */
  const handleRequestClose = useCallback(
    (rowId: number) => {
      collapseRow();
      toggleRefs.current.get(rowId)?.focus();
    },
    [collapseRow]
  );

  const toggleSelected = useCallback((id: number) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectableIds = useMemo(
    () => SELECTABLE_GROUPS.flatMap((g) => grouped[g].map((r) => r.id)),
    [grouped],
  );
  const isGroupAllSelected = useCallback(
    (g: keyof GroupedResults) => {
      const ids = grouped[g].map((r) => r.id);
      return ids.length > 0 && ids.every((id) => selectedIds.has(id));
    },
    [grouped, selectedIds],
  );
  const toggleSelectAllGroup = useCallback(
    (g: keyof GroupedResults) => {
      setSelectedIds((cur) => {
        const ids = grouped[g].map((r) => r.id);
        const next = new Set(cur);
        const all = ids.length > 0 && ids.every((id) => next.has(id));
        for (const id of ids) {
          if (all) next.delete(id);
          else next.add(id);
        }
        return next;
      });
    },
    [grouped],
  );

  // Prune ids that no longer belong to a selectable section (e.g. a resolution
  // mutation re-bucketed the row on the last settled fetch) so the selection
  // never references a row that's gone. Returns the same `Set` instance when
  // nothing changed, so this doesn't cause an extra render on unrelated updates.
  useEffect(() => {
    const validIds = new Set(selectableIds);
    setSelectedIds((cur) => {
      if (cur.size === 0) return cur;
      let changed = false;
      const next = new Set<number>();
      for (const id of cur) {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : cur;
    });
  }, [selectableIds]);

  const bulkResolve = useBulkResolve(runId);
  const selectedCount = selectedIds.size;

  /**
   * Bulk-resolve outcome announcement (Task 3, 2026-08-19 remediation Slice
   * B). Bulk-resolving clears `selectedIds` on success, which un-mounts the
   * `selectedCount > 0` toolbar below — so this region lives OUTSIDE that
   * conditional (rendered unconditionally near the top of this component's
   * JSX) rather than inside it: a live region that gets removed from the DOM
   * in the same render where its text changes is a textbook way for the
   * announcement to silently never fire.
   */
  const [bulkAnnouncement, setBulkAnnouncement] = useState<{
    text: string;
    politeness: 'polite' | 'assertive';
  }>({ text: '', politeness: 'polite' });

  function runBulkResolve(resolution: 'accepted' | 'no_match_confirmed') {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkResolve.mutate(
      { ids, resolution },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          const label = ids.length === 1 ? 'rând validat' : 'rânduri validate';
          setBulkAnnouncement({ text: `${formatNumberRo(ids.length)} ${label}`, politeness: 'polite' });
        },
        onError: (error) => {
          setBulkAnnouncement({
            text: error instanceof Error ? error.message : 'Validarea în masă a eșuat.',
            politeness: 'assertive',
          });
        },
      }
    );
  }

  // Task 6: retry-failed-rows. `retryCandidates` mirrors v1's
  // `rowsNeedingRetry` scope — errored rows AND classified-but-empty rows,
  // over the WHOLE (already-filtered) `rows` prop, not just the „Erori"
  // group — so the header count (N) and the set of rows carrying a per-row
  // „Reîncearcă" button agree with each other regardless of which of the
  // four groups a candidate row actually renders under.
  const retryMutation = useRetryRows(runId);
  const retryOrchestrator = useRetryOrchestrator(retryMutation);
  const retryCandidates = useMemo(() => rowsNeedingRetry(rows), [rows]);
  const retryCandidateIds = useMemo(() => new Set(retryCandidates.map((r) => r.id)), [retryCandidates]);

  const table = useReactTable({
    data: allRows,
    columns,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
  });
  const rowsById = table.getRowModel().rowsById;

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: renderItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => estimateItemSize(renderItems[index]),
    getItemKey: (index) => renderItems[index].key,
    overscan: 10,
  });

  if (rows.length === 0) return null;

  return (
    // I3 (2026-08-20): `pr-[23rem]` (368px) matches the session drawer's
    // measured on-screen footprint from the viewport's right edge (352px
    // wide, 16px off the edge) — reserved ONLY in the `'drawer'` phase (see
    // `drawerPhase` above), so whatever of the grid actually paints on screen
    // (scrolled or not) ends before the drawer's left edge instead of sitting
    // under it. Deliberately scoped to a real drawer, not every corner state:
    // the `'prompt'` phase is a slim `size-12` FAB that clears STATUS on its
    // own, so reserving 368px for it would cost the grid that width — and the
    // ~344px of forced horizontal scroll that comes with it — for nothing.
    <div className={`mt-6 ${drawerPhase === 'drawer' ? 'xl:pr-[23rem]' : ''}`}>
      <LiveRegion message={bulkAnnouncement.text} politeness={bulkAnnouncement.politeness} />
      {!readOnly && selectedCount > 0 && (
        <div className="sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-teal bg-teal-wash px-4 py-2.5">
          <span className="text-[13.5px] text-ink-2">
            <span className="font-semibold tabular-nums text-ink">{formatNumberRo(selectedCount)}</span> selectate
          </span>
          <Button type="button" size="sm" onClick={() => runBulkResolve('accepted')} disabled={bulkResolve.isPending}>
            Validează selectate ({formatNumberRo(selectedCount)})
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => runBulkResolve('no_match_confirmed')}
            disabled={bulkResolve.isPending}
          >
            Fără corespondent ({formatNumberRo(selectedCount)})
          </Button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkResolve.isPending}
            className="ml-auto text-[13.5px] font-medium text-ink-3 hover:text-ink-2 disabled:opacity-50"
          >
            Anulează selecția
          </button>
          {/* Fix 5 (review mutations fail silently): a bulk-resolve failure
              (e.g. a 409 — the run got finalized concurrently) previously
              rendered nothing, leaving the operator to guess why the
              selection didn't clear. Surface the server's Romanian message
              (via `ApiClientError`) inline, no modal. */}
          {bulkResolve.isError && (
            <span className="text-[13.5px] text-bad">{bulkResolve.error?.message}</span>
          )}
        </div>
      )}

      {/* Horizontal reflow. GRID_TEMPLATE's fixed columns total ~780px with gaps,
          so under that the table used to clip instead of scroll — unlike
          MapariListPage/NomenclatorPage, which already wrap theirs. Header and
          body share ONE overflow-x container so they scroll in lockstep and stay
          column-aligned; the body keeps its own vertical scroll inside. The
          correction dropdown is `absolute w-full` (never wider than its row), so
          it isn't clipped by this container. */}
      <div className="overflow-x-auto">
      <div
        className="min-w-[1080px]"
        role="grid"
        aria-rowcount={allRows.length + 1}
        aria-label="Rezultatele mapării"
      >
      <div
        className="grid items-center gap-2 border-b border-rule px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3"
        style={{ gridTemplateColumns: GRID_TEMPLATE }}
        role="row"
        aria-rowindex={1}
      >
        {/* Code review (2026-08-19, item 1): these two filler slots line up
            with every data row's leading checkbox cell and trailing
            actions cell (both role="gridcell" — never aria-hidden). Browsers
            compute aria-colindex by counting cell-role SIBLINGS within the
            row when no explicit aria-colindex is set, so an aria-hidden
            filler here (removed from the accessibility tree) shifted every
            real column's computed index by one against the data rows below
            it — „Cod MEDOC" was announced under the „Denumire MEDOC"
            header, etc. Both rows must expose the same 9 positionally
            aligned cells. */}
        <div role="columnheader" aria-label="Selectare" />
        {table.getFlatHeaders().map((header) => (
          <div
            key={header.id}
            role="columnheader"
            className="truncate"
            title={typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : undefined}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        ))}
        <div role="columnheader" aria-label="Acțiuni" />
      </div>

      <div ref={parentRef} className="max-h-[65vh] overflow-y-auto rounded-b-xl border border-t-0 border-rule bg-bg">
        <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const item = renderItems[virtualRow.index];
            return (
              <div
                key={item.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                // Fix round item 3 (2026-08-19 review): `role="grid"`'s only
                // valid owned elements are `row`/`rowgroup` — section header
                // banners, the „Rezolvate" collapsed-toggle, and detail
                // panels previously sat here with no row role at all. The
                // `'row'` branch below already carries `role="row"` on its
                // own inner grid-template div (one level deeper, through
                // this same unroled wrapper — the standard virtualized-grid
                // pattern, per Task 2's report), so it's deliberately
                // excluded here to avoid nesting one `row` inside another
                // (itself invalid — `row`'s owned elements are cells, not
                // rows). For the other three types, this outer wrapper is
                // the only element common to all of them, so it carries the
                // role directly — an attribute addition on an element the
                // virtualizer already measures, not a new element, so it
                // cannot change what gets measured.
                role={item.type === 'row' ? undefined : 'row'}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                  // `transform` creates a new stacking context per CSS Transforms spec, so a
                  // detail panel's absolutely-positioned nomenclator dropdown can't escape its
                  // own wrapper's rank to paint above LATER row/header wrappers below it. Raise
                  // the detail wrapper's rank so the overflowing dropdown stays on top. (Two
                  // adjacent expanded details share rank 1 — DOM order decides; acceptable.)
                  zIndex: item.type === 'detail' ? 1 : 0,
                }}
              >
                {item.type === 'header' && (
                  <HeaderItem
                    item={item}
                    onCollapseRezolvate={() => setRezolvateExpanded(false)}
                    allSelected={item.selectable ? isGroupAllSelected(item.group) : false}
                    onToggleSelectAll={() => toggleSelectAllGroup(item.group)}
                    readOnly={readOnly}
                    retrySlot={
                      item.key === 'header-erori' && !readOnly ? (
                        <RetryBar candidates={retryCandidates} orchestrator={retryOrchestrator} />
                      ) : undefined
                    }
                  />
                )}

                {item.type === 'collapsed-toggle' && (
                  // Fix round item 3: the button can't itself carry
                  // `role="gridcell"` (that would replace its native button
                  // role in the accessibility tree, so a screen reader would
                  // no longer announce it as activatable) — a gridcell
                  // CONTAINING an interactive button, not a button acting as
                  // a gridcell, is the standard composite pattern.
                  // `className="contents"`
                  // (`display:contents`) removes this wrapper from the box
                  // tree entirely — it contributes zero size, so it cannot
                  // change what `measureElement` reads on the ancestor
                  // wrapper; the button's own layout is untouched.
                  <div role="gridcell" className="contents">
                    <button
                      type="button"
                      onClick={() => setRezolvateExpanded(true)}
                      className="flex w-full items-center justify-center gap-2 border-b border-rule-soft bg-wash py-3.5 text-[14px] font-semibold text-teal transition-colors hover:bg-teal-wash"
                    >
                      <ChevronDown className="size-4" />
                      {item.label}
                    </button>
                  </div>
                )}

                {item.type === 'row' &&
                  (() => {
                    const tableRow = rowsById[String(item.row.id)];
                    if (!tableRow) return null;
                    return (
                      <div
                        className="grid h-full items-center gap-2 border-b border-rule-soft px-4 py-2 text-[13.5px] text-ink-2 hover:bg-wash"
                        style={{ gridTemplateColumns: GRID_TEMPLATE }}
                        role="row"
                        aria-rowindex={(rowIndexById.get(item.row.id) ?? 0) + 2}
                      >
                        <div role="gridcell" className="flex items-center justify-center">
                          {SELECTABLE_GROUPS.includes(item.group) && !readOnly ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(item.row.id)}
                              onChange={() => toggleSelected(item.row.id)}
                              aria-label={`Selectează „${item.row.name}" pentru validare în masă`}
                              className="size-4 accent-teal"
                            />
                          ) : (
                            <span aria-hidden />
                          )}
                        </div>
                        {tableRow.getVisibleCells().map((cell) => (
                          <div key={cell.id} role="gridcell" className="min-w-0 truncate">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        ))}
                        <div role="gridcell" className="flex items-center justify-end gap-0.5">
                          {!readOnly && retryCandidateIds.has(item.row.id) && (
                            <RetryRowButton row={item.row} orchestrator={retryOrchestrator} />
                          )}
                          <RowExpandToggle
                            ref={registerToggleRef(item.row.id)}
                            group={item.group}
                            expanded={expandedRowId === item.row.id}
                            onToggle={() => toggleExpand(item.row.id)}
                            panelId={panelId(item.row.id)}
                          />
                        </div>
                      </div>
                    );
                  })()}

                {item.type === 'detail' && (
                  <DetailItem
                    item={item}
                    runId={runId}
                    correcting={correctingRezolvatId === item.row.id}
                    onStartCorrecting={() => setCorrectingRezolvatId(item.row.id)}
                    onDone={collapseRow}
                    onRequestClose={() => handleRequestClose(item.row.id)}
                    readOnly={readOnly}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      </div>
      </div>
    </div>
  );
}
